import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkflowStore } from '../store/workflow';
import { useSettingsStore } from '../store/settings';
import { useChatStore } from '../store/chat';
import { useMCPStore } from '../store/mcp';
import { createApiClient, type WorkflowSummary } from '@n8n-builder/api-client';
import { WorkflowCanvas } from '../components/workflow/WorkflowCanvas';
import { NodeIcon } from '../components/NodeIcon';
import { formatRelativeDate } from '../utils/formatDate';
import { API_BASE_URL } from '../lib/api-config';


// Icon Components
function CubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

function RocketIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
    </svg>
  );
}

function MagnifyingGlassIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function EllipsisVerticalIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
    </svg>
  );
}

function ArrowTopRightOnSquareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
    </svg>
  );
}

function PencilSquareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  );
}


interface DeployResult {
  type: 'success' | 'error' | 'warning';
  message: string;
  workflowUrl?: string;
}

interface DeleteConfirmation {
  workflow: WorkflowSummary;
  isDeployed: boolean;
}

type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc';
type FilterOption = 'all' | 'deployed' | 'draft';

export function WorkflowsPage() {
  const { workflows, setWorkflows, removeWorkflow, isLoading, currentWorkflow, setCurrentWorkflow, fetchWorkflows } =
    useWorkflowStore();
  const { activeInstance, hasValidCredentials, setChatMode, fetchActiveInstance } = useSettingsStore();
  const { loadConversation, startNewConversation, setAttachedWorkflow } = useChatStore();
  const { instances, fetchInstances } = useMCPStore();
  const navigate = useNavigate();

  const [viewingWorkflow, setViewingWorkflow] = useState<WorkflowSummary | null>(null);
  const [loadingWorkflowId, setLoadingWorkflowId] = useState<string | null>(null);
  const [deletingWorkflowId, setDeletingWorkflowId] = useState<string | null>(null);
  const [deployingWorkflowId, setDeployingWorkflowId] = useState<string | null>(null);
  const [deployResult, setDeployResult] = useState<DeployResult | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);
  const [editingWorkflowId, setEditingWorkflowId] = useState<string | null>(null);

  // Three-dots dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Search, sort, and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [filterInstance, setFilterInstance] = useState<string>('all');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showInstanceDropdown, setShowInstanceDropdown] = useState(false);

  const getApiClient = () => {
    return createApiClient(API_BASE_URL);
  };

  // Load workflows and active instance on mount (cache-aware)
  useEffect(() => {
    fetchWorkflows();
    fetchActiveInstance();
    fetchInstances();
  }, []);

  // Auto-dismiss deploy result
  useEffect(() => {
    if (deployResult) {
      const timer = setTimeout(() => setDeployResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [deployResult]);

  // Close sort dropdown on outside click
  useEffect(() => {
    if (!showSortDropdown) return;

    const handleClick = () => setShowSortDropdown(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showSortDropdown]);

  // Close instance dropdown on outside click
  useEffect(() => {
    if (!showInstanceDropdown) return;

    const handleClick = () => setShowInstanceDropdown(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showInstanceDropdown]);

  // Close card dropdown on outside click
  useEffect(() => {
    if (!openDropdownId) return;

    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openDropdownId]);

  // Filter and sort workflows
  const filteredWorkflows = useMemo(() => {
    let result = [...workflows];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((w) => w.name.toLowerCase().includes(query));
    }

    // Filter by status
    if (filterBy === 'deployed') {
      result = result.filter((w) => w.n8nWorkflowId);
    } else if (filterBy === 'draft') {
      result = result.filter((w) => !w.n8nWorkflowId);
    }

    // Filter by instance
    if (filterInstance !== 'all') {
      result = result.filter((w) => w.deployedToInstanceId === filterInstance);
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case 'oldest':
        result.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }

    return result;
  }, [workflows, searchQuery, filterBy, filterInstance, sortBy]);

  // Counts for filter tabs (scoped to instance + search filters)
  const counts = useMemo(() => {
    let base = workflows;
    if (filterInstance !== 'all') {
      base = base.filter((w) => w.deployedToInstanceId === filterInstance);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      base = base.filter((w) => w.name.toLowerCase().includes(query));
    }
    const deployedCount = base.filter((w) => w.n8nWorkflowId).length;
    return {
      all: base.length,
      deployed: deployedCount,
      draft: base.length - deployedCount,
    };
  }, [workflows, filterInstance, searchQuery]);

  const handleView = async (workflow: WorkflowSummary) => {
    setLoadingWorkflowId(workflow.id);
    try {
      const apiClient = getApiClient();
      const result = await apiClient.getWorkflow(workflow.id);
      if (result.data?.workflow) {
        setCurrentWorkflow(result.data.workflow);
        setViewingWorkflow(workflow);
      }
    } catch (error) {
      console.error('Failed to load workflow:', error);
    } finally {
      setLoadingWorkflowId(null);
    }
  };

  const handleCloseView = () => {
    setViewingWorkflow(null);
    setCurrentWorkflow(null);
  };

  const handleDeleteClick = (workflow: WorkflowSummary) => {
    setDeleteConfirmation({
      workflow,
      isDeployed: !!workflow.n8nWorkflowId,
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation(null);
  };

  const handleDeleteConfirm = async (deleteFromN8n: boolean) => {
    if (!deleteConfirmation) return;

    const { workflow } = deleteConfirmation;
    setDeleteConfirmation(null);
    setDeletingWorkflowId(workflow.id);

    try {
      const apiClient = getApiClient();

      // Try to delete from n8n first if requested
      if (deleteFromN8n && workflow.n8nWorkflowId) {
        const n8nResult = await apiClient.deleteFromN8n(workflow.id);
        if (!n8nResult.data?.success && n8nResult.error) {
          // Show warning but continue with local delete
          setDeployResult({
            type: 'warning',
            message: `Failed to delete from n8n: ${n8nResult.error}. Removing from builder.`,
          });
        }
      }

      // Delete from builder (D1 + R2)
      const result = await apiClient.deleteWorkflow(workflow.id);
      if (result.data?.success) {
        removeWorkflow(workflow.id);
        if (viewingWorkflow?.id === workflow.id) {
          handleCloseView();
        }
      }
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      setDeployResult({
        type: 'error',
        message: error instanceof Error ? error.message : 'Delete failed',
      });
    } finally {
      setDeletingWorkflowId(null);
    }
  };

  const handleDeploy = useCallback(async (workflow: WorkflowSummary) => {
    setDeployResult(null);

    if (!hasValidCredentials()) {
      setDeployResult({
        type: 'error',
        message: 'No n8n instance configured. Go to Settings or MCP to configure one.',
      });
      return;
    }

    setDeployingWorkflowId(workflow.id);
    try {
      const apiClient = getApiClient();

      // First fetch the full workflow
      const workflowResult = await apiClient.getWorkflow(workflow.id);
      if (!workflowResult.data?.workflow) {
        setDeployResult({ type: 'error', message: 'Failed to load workflow' });
        return;
      }

      const result = await apiClient.deployWorkflow(workflowResult.data.workflow, workflow.id);

      if (result.error) {
        setDeployResult({ type: 'error', message: result.error });
      } else if (result.data?.success) {
        // Update local workflow state with n8n workflow ID + instance info
        const updatedWorkflows = workflows.map((w) =>
          w.id === workflow.id
            ? {
                ...w,
                n8nWorkflowId: result.data!.n8nWorkflowId,
                deployedToInstanceId: result.data!.instanceId || null,
                deployedToInstanceName: result.data!.instanceName || null,
              }
            : w
        );
        setWorkflows(updatedWorkflows);

        setDeployResult({
          type: 'success',
          message: 'Workflow deployed successfully!',
          workflowUrl: result.data.workflowUrl,
        });
      } else {
        setDeployResult({
          type: 'error',
          message: result.data?.error || 'Deployment failed',
        });
      }
    } catch (error) {
      setDeployResult({
        type: 'error',
        message: error instanceof Error ? error.message : 'Deployment failed',
      });
    } finally {
      setDeployingWorkflowId(null);
    }
  }, [hasValidCredentials, workflows, setWorkflows]);

  const dismissResult = () => setDeployResult(null);

  // Handle "View in n8n" - opens workflow in user's n8n instance
  const handleViewInN8n = (workflow: WorkflowSummary) => {
    if (activeInstance?.url && workflow.n8nWorkflowId) {
      window.open(`${activeInstance.url}/workflow/${workflow.n8nWorkflowId}`, '_blank');
    }
  };

  // Check if "View in n8n" should be enabled
  const canViewInN8n = (workflow: WorkflowSummary) => {
    return activeInstance?.url && workflow.n8nWorkflowId;
  };

  // Get tooltip for View in n8n button when disabled
  const getViewInN8nTooltip = (workflow: WorkflowSummary) => {
    if (!activeInstance?.url) return 'Connect n8n instance first';
    if (!workflow.n8nWorkflowId) return 'Deploy first to view in n8n';
    return '';
  };

  // Handle "Edit" - navigates to workflow builder with workflow loaded
  const handleEdit = async (workflow: WorkflowSummary) => {
    setEditingWorkflowId(workflow.id);
    try {
      // Fetch full workflow data
      const apiClient = getApiClient();
      const result = await apiClient.getWorkflow(workflow.id);
      if (!result.data?.workflow) {
        setEditingWorkflowId(null);
        return;
      }

      const fullWorkflow = result.data.workflow;

      if (workflow.conversationId) {
        // Load existing conversation
        const convResult = await apiClient.getConversation(workflow.conversationId);
        if (convResult.data) {
          loadConversation(workflow.conversationId, convResult.data.messages, new Map([[workflow.id, fullWorkflow]]));
        }
      } else {
        // Start new conversation with attached workflow
        startNewConversation();
        setAttachedWorkflow({
          filename: workflow.name,
          workflow: fullWorkflow,
          nodeCount: workflow.nodeCount,
        });
      }

      setChatMode('edit');
      navigate('/workflow-builder');
    } catch (error) {
      console.error('Failed to load workflow for editing:', error);
    } finally {
      setEditingWorkflowId(null);
    }
  };

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'name-asc', label: 'Name A-Z' },
    { value: 'name-desc', label: 'Name Z-A' },
  ];

  return (
    <div className="min-h-full bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <FolderIcon className="h-4 w-4" />
          <span>Library</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Your Workflows</h1>
        <p className="mt-1 text-gray-500">Manage your generated n8n workflows</p>
      </header>

      {/* Search, Sort, Filter Bar */}
      <div className="mb-6 space-y-4">
        {/* Search and Sort Row */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Bar */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Instance Filter Dropdown */}
          {instances.length > 0 && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInstanceDropdown(!showInstanceDropdown);
                }}
                className="flex w-48 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {filterInstance !== 'all' && (
                  <span
                    className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: instances.find((i) => i.id === filterInstance)?.color || '#9ca3af' }}
                  />
                )}
                <span className="truncate">{filterInstance === 'all' ? 'All instances' : instances.find((i) => i.id === filterInstance)?.name}</span>
                <ChevronDownIcon className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400" />
              </button>

              {showInstanceDropdown && (
                <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <button
                    onClick={() => {
                      setFilterInstance('all');
                      setShowInstanceDropdown(false);
                    }}
                    className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm ${
                      filterInstance === 'all'
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    All instances
                  </button>
                  {instances.map((inst) => (
                    <button
                      key={inst.id}
                      onClick={() => {
                        setFilterInstance(inst.id);
                        setShowInstanceDropdown(false);
                      }}
                      className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm ${
                        filterInstance === inst.id
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: inst.color || '#9ca3af' }}
                      />
                      <span className="truncate">{inst.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sort Dropdown */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowSortDropdown(!showSortDropdown);
              }}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <span>{sortOptions.find((o) => o.value === sortBy)?.label}</span>
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            </button>

            {showSortDropdown && (
              <div className="absolute right-0 z-10 mt-1 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setShowSortDropdown(false);
                    }}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      sortBy === option.value
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1 w-fit">
          {(['all', 'deployed', 'draft'] as FilterOption[]).map((filter) => (
            <button
              key={filter}
              onClick={() => setFilterBy(filter)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                filterBy === filter
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
              <span className="ml-1.5 text-gray-400">({counts[filter]})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Deploy/Delete Result Toast */}
      {deployResult && (
        <div className="fixed right-6 top-6 z-50 toast-enter">
          <div
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 shadow-lg ${
              deployResult.type === 'success'
                ? 'border-green-200 bg-green-50'
                : deployResult.type === 'warning'
                ? 'border-yellow-200 bg-yellow-50'
                : 'border-red-200 bg-red-50'
            }`}
          >
            <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
              deployResult.type === 'success'
                ? 'bg-green-100'
                : deployResult.type === 'warning'
                ? 'bg-yellow-100'
                : 'bg-red-100'
            }`}>
              {deployResult.type === 'success' ? (
                <CheckIcon className="h-4 w-4 text-green-600" />
              ) : deployResult.type === 'warning' ? (
                <XMarkIcon className="h-4 w-4 text-yellow-600" />
              ) : (
                <XMarkIcon className="h-4 w-4 text-red-600" />
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                deployResult.type === 'success'
                  ? 'text-green-800'
                  : deployResult.type === 'warning'
                  ? 'text-yellow-800'
                  : 'text-red-800'
              }`}>
                {deployResult.message}
              </p>
              {deployResult.workflowUrl && (
                <a
                  href={deployResult.workflowUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-green-700 underline hover:text-green-900"
                >
                  Open in n8n
                </a>
              )}
            </div>
            <button
              onClick={dismissResult}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-48 rounded-xl skeleton"
            />
          ))}
        </div>
      ) : workflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100">
            <CubeIcon className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No workflows yet</h3>
          <p className="mt-1 text-center text-gray-500">
            Start a chat to generate your first workflow
          </p>
        </div>
      ) : filteredWorkflows.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100">
            <MagnifyingGlassIcon className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No matching workflows</h3>
          <p className="mt-1 text-center text-gray-500">
            {searchQuery
              ? `No workflows found matching "${searchQuery}"`
              : `No ${filterBy === 'deployed' ? 'deployed' : 'draft'} workflows`}
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setFilterBy('all');
              setFilterInstance('all');
            }}
            className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredWorkflows.map((workflow) => (
            <div
              key={workflow.id}
              onClick={() => handleView(workflow)}
              className={`group relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5 cursor-pointer ${
                loadingWorkflowId === workflow.id ? 'pointer-events-none opacity-75' : ''
              }`}
            >
              {/* Top Row: Integration Icons, Status Badge & Three-dots Menu */}
              <div className="flex items-center justify-between mb-4">
                {/* Integration Icons */}
                <div className="flex items-center gap-1.5">
                  {workflow.nodeTypes && workflow.nodeTypes.length > 0 ? (
                    workflow.nodeTypes.slice(0, 4).map((nodeType, index) => (
                      <NodeIcon key={index} nodeType={nodeType} size={24} />
                    ))
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-100">
                      <CubeIcon className="h-3.5 w-3.5 text-gray-500" />
                    </div>
                  )}
                  {workflow.nodeTypes && workflow.nodeTypes.length > 4 && (
                    <span className="text-xs text-gray-400 ml-1">
                      +{workflow.nodeTypes.length - 4}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Instance Badge */}
                  {workflow.deployedToInstanceName && (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {workflow.deployedToInstanceName}
                    </span>
                  )}
                  {/* Deployed Badge */}
                  {workflow.n8nWorkflowId && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                      <CheckIcon className="h-3 w-3" />
                      Deployed
                    </span>
                  )}

                  {/* Three-dots Dropdown Menu */}
                  <div className="relative" ref={openDropdownId === workflow.id ? dropdownRef : undefined}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId(openDropdownId === workflow.id ? null : workflow.id);
                      }}
                      className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    >
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </button>

                    {openDropdownId === workflow.id && (
                      <div className="absolute right-0 z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdownId(null);
                            handleDeploy(workflow);
                          }}
                          disabled={deployingWorkflowId === workflow.id}
                          className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <RocketIcon className="h-4 w-4" />
                          {deployingWorkflowId === workflow.id ? 'Deploying...' : `Deploy to ${activeInstance?.name || 'n8n'}`}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdownId(null);
                            handleDeleteClick(workflow);
                          }}
                          disabled={deletingWorkflowId === workflow.id}
                          className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                          {deletingWorkflowId === workflow.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Workflow Name */}
              <h3 className="font-semibold text-gray-900 text-base mb-1 line-clamp-2">
                {workflow.name}
              </h3>

              {/* Meta Info */}
              <p className="text-sm text-gray-500">
                {workflow.nodeCount} nodes
                <span className="mx-1.5">·</span>
                Created {formatRelativeDate(workflow.createdAt)}
              </p>

              {/* Action Buttons - View in n8n & Edit */}
              <div className="mt-4 flex flex-wrap gap-2 justify-end opacity-0 transition-opacity group-hover:opacity-100 sm:opacity-0">
                <button
                  onClick={(e) => { e.stopPropagation(); handleViewInN8n(workflow); }}
                  disabled={!canViewInN8n(workflow)}
                  title={getViewInN8nTooltip(workflow)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  View in n8n
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEdit(workflow); }}
                  disabled={editingWorkflowId === workflow.id}
                  className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  {editingWorkflowId === workflow.id ? 'Loading...' : 'Edit'}
                </button>
              </div>

              {/* Always visible on mobile */}
              <div className="mt-4 flex flex-wrap gap-2 justify-end sm:hidden">
                <button
                  onClick={(e) => { e.stopPropagation(); handleViewInN8n(workflow); }}
                  disabled={!canViewInN8n(workflow)}
                  title={getViewInN8nTooltip(workflow)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  View in n8n
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEdit(workflow); }}
                  disabled={editingWorkflowId === workflow.id}
                  className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  {editingWorkflowId === workflow.id ? 'Loading...' : 'Edit'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Workflow View Modal */}
      {viewingWorkflow && currentWorkflow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl animate-fade-in">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50">
                  <CubeIcon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{currentWorkflow.name}</h2>
                  <p className="text-sm text-gray-500">{currentWorkflow.nodes.length} nodes</p>
                </div>
              </div>
              <button
                onClick={handleCloseView}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content - Visual Preview */}
            <div className="h-[60vh] p-4 flex flex-col">
              <WorkflowCanvas workflow={currentWorkflow} tidyUp={true} />
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 border-t border-gray-200 px-6 py-4">
              {/* Left side actions */}
              <button
                onClick={() => handleViewInN8n(viewingWorkflow)}
                disabled={!canViewInN8n(viewingWorkflow)}
                title={getViewInN8nTooltip(viewingWorkflow)}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                View in n8n
              </button>
              <button
                onClick={() => { handleCloseView(); handleEdit(viewingWorkflow); }}
                disabled={editingWorkflowId === viewingWorkflow.id}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                <PencilSquareIcon className="h-4 w-4" />
                {editingWorkflowId === viewingWorkflow.id ? 'Loading...' : 'Edit'}
              </button>
              <button
                onClick={() => { handleCloseView(); handleDeleteClick(viewingWorkflow); }}
                disabled={deletingWorkflowId === viewingWorkflow.id}
                className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Right side deploy action */}
              <button
                onClick={() => handleDeploy(viewingWorkflow)}
                disabled={deployingWorkflowId === viewingWorkflow.id}
                className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
              >
                <RocketIcon className="h-4 w-4" />
                {deployingWorkflowId === viewingWorkflow.id ? 'Deploying...' : `Deploy to ${activeInstance?.name || 'n8n'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl animate-fade-in">
            {/* Modal Header */}
            <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50">
                <TrashIcon className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Delete Workflow</h2>
                <p className="text-sm text-gray-500">{deleteConfirmation.workflow.name}</p>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4">
              {deleteConfirmation.isDeployed ? (
                <p className="text-sm text-gray-600">
                  This workflow has been deployed to your n8n instance. Would you like to delete it
                  from n8n as well, or only remove it from the builder?
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this workflow? This action cannot be undone.
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={handleDeleteCancel}
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              {deleteConfirmation.isDeployed ? (
                <>
                  <button
                    onClick={() => handleDeleteConfirm(false)}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Builder only
                  </button>
                  <button
                    onClick={() => handleDeleteConfirm(true)}
                    className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700"
                  >
                    Delete from both
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleDeleteConfirm(false)}
                  className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
